(() => {
  const STORAGE_KEY = 'tomokagu:wishlist:v1';
  const UPDATE_EVENT = 'tomokagu:wishlist:updated';

  const read = () => {
    try {
      const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      return Array.isArray(value) ? value.filter((item) => item && item.handle) : [];
    } catch (_) {
      return [];
    }
  };

  const write = (items) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    window.dispatchEvent(new CustomEvent(UPDATE_EVENT, { detail: { items } }));
  };

  const sameProduct = (item, product) => String(item.id || '') === String(product.id || '') || item.handle === product.handle;
  const productFrom = (element) => ({
    id: String(element.dataset.productId || ''),
    handle: element.dataset.productHandle || '',
    title: element.dataset.productTitle || '',
    url: element.dataset.productUrl || ''
  });

  class WishlistCount extends HTMLElement {
    connectedCallback() {
      this.update(read());
      window.addEventListener(UPDATE_EVENT, this);
      window.addEventListener('storage', this);
    }
    disconnectedCallback() {
      window.removeEventListener(UPDATE_EVENT, this);
      window.removeEventListener('storage', this);
    }
    handleEvent(event) {
      if (event.type !== 'storage' || event.key === STORAGE_KEY) this.update(event.detail?.items || read());
    }
    update(items) {
      this.hidden = items.length === 0;
      this.textContent = items.length > 99 ? '99+' : String(items.length);
      this.setAttribute('aria-label', `お気に入り ${items.length}件`);
    }
  }

  class WishlistToggle extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.label = this.querySelector('[data-wishlist-label]');
      this.product = productFrom(this);
      if (!this.button || !this.product.handle) return;
      this.button.addEventListener('click', this);
      window.addEventListener(UPDATE_EVENT, this);
      window.addEventListener('storage', this);
      this.sync(read());
    }
    disconnectedCallback() {
      this.button?.removeEventListener('click', this);
      window.removeEventListener(UPDATE_EVENT, this);
      window.removeEventListener('storage', this);
    }
    handleEvent(event) {
      if (event.type === 'click') {
        event.preventDefault();
        event.stopPropagation();
        const items = read();
        write(items.some((item) => sameProduct(item, this.product))
          ? items.filter((item) => !sameProduct(item, this.product))
          : [...items, this.product]);
        return;
      }
      if (event.type !== 'storage' || event.key === STORAGE_KEY) this.sync(event.detail?.items || read());
    }
    sync(items) {
      const active = items.some((item) => sameProduct(item, this.product));
      const text = active ? this.dataset.removeLabel : this.dataset.addLabel;
      this.toggleAttribute('active', active);
      this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
      this.button.setAttribute('aria-label', text);
      if (this.label) this.label.textContent = text;
    }
  }

  const money = (cents, currency) => new Intl.NumberFormat(document.documentElement.lang || 'ja-JP', {
    style: 'currency', currency: currency || 'JPY', maximumFractionDigits: 0
  }).format(Number(cents || 0) / 100);

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.loading = this.querySelector('[data-wishlist-loading]');
      this.count = this.querySelector('[data-wishlist-page-count]');
      window.addEventListener(UPDATE_EVENT, this);
      window.addEventListener('storage', this);
      this.render();
    }
    disconnectedCallback() {
      window.removeEventListener(UPDATE_EVENT, this);
      window.removeEventListener('storage', this);
    }
    handleEvent(event) {
      if (event.type !== 'storage' || event.key === STORAGE_KEY) this.render();
    }
    async render() {
      const items = read();
      this.count.textContent = `${items.length}件の商品`;
      this.grid.innerHTML = '';
      this.empty.hidden = items.length > 0;
      this.loading.hidden = items.length === 0;
      if (!items.length) return;
      const products = (await Promise.all(items.map(async (item) => {
        try {
          const response = await fetch(`/products/${encodeURIComponent(item.handle)}.js`);
          return response.ok ? response.json() : null;
        } catch (_) { return null; }
      }))).filter(Boolean);
      this.loading.hidden = true;
      this.empty.hidden = products.length > 0;
      products.forEach((product) => this.grid.insertAdjacentHTML('beforeend', this.card(product)));
    }
    card(product) {
      const image = product.featured_image || product.images?.[0] || '';
      const url = product.url || `/products/${product.handle}`;
      return `<article class="tomo-wishlist-card">
        <a class="tomo-wishlist-card__media" href="${url}">${image ? `<img src="${image}" alt="${this.escape(product.title)}" loading="lazy">` : ''}</a>
        <div class="tomo-wishlist-card__body">
          <h2><a href="${url}">${this.escape(product.title)}</a></h2>
          <p class="tomo-wishlist-card__price">${money(product.price, this.dataset.currency)}</p>
          <a class="tomo-wishlist-card__details" href="${url}">詳細を見る</a>
        </div>
        <tomokagu-wishlist-toggle class="tomo-wishlist-toggle tomo-wishlist-toggle--remove" data-product-id="${product.id}" data-product-handle="${this.escape(product.handle)}" data-product-title="${this.escape(product.title)}" data-product-url="${url}" data-add-label="お気に入りに追加" data-remove-label="お気に入りから削除">
          <button type="button" class="tomo-wishlist-toggle__button" aria-pressed="true" aria-label="お気に入りから削除"><span aria-hidden="true">×</span></button>
        </tomokagu-wishlist-toggle>
      </article>`;
    }
    escape(value) {
      const node = document.createElement('div');
      node.textContent = value || '';
      return node.innerHTML;
    }
  }

  if (!customElements.get('tomokagu-wishlist-count')) customElements.define('tomokagu-wishlist-count', WishlistCount);
  if (!customElements.get('tomokagu-wishlist-toggle')) customElements.define('tomokagu-wishlist-toggle', WishlistToggle);
  if (!customElements.get('tomokagu-wishlist-page')) customElements.define('tomokagu-wishlist-page', WishlistPage);
})();
